import React from "react";
import { Checkbox } from "@mui/material";
import { MRT_Row, MRT_ColumnDef } from "material-react-table";
import type { Job } from "../../../../../../api/types";
import { ImportTableColumn } from "../../../../../../utilities/types";
import { columnDisplayNames } from "../../../../../admin/ImportSettings";
import { ColumnContext } from "../ColumnContext";
import { BooleanColumnOptions, CheckboxColumnViewModel, IndividualCheckboxChangeOptions } from "./checkboxColumn.types";
import { useCheckboxColumnViewModel } from "./checkboxColumn.vm";

interface CheckboxColumnCellProps {
	row: MRT_Row<Job>;
	vm: CheckboxColumnViewModel;
	fieldKey: keyof Job;
	groupByField: keyof Job;
	options: IndividualCheckboxChangeOptions;
}

/**
 * Memoized Cell component for checkbox columns
 */
const CheckboxColumnCell = React.memo(
	function CheckboxColumnCell({ row, vm, fieldKey, groupByField, options }: CheckboxColumnCellProps) {
		const job = row.original;
		const currentValue = vm.getCurrentCheckboxValue(job, fieldKey);
		const groupValue = String(job[groupByField] ?? "");

		return (
			<Checkbox
				checked={currentValue}
				onChange={(event) => {
					vm.handleIndividualCheckboxChange(job.id, fieldKey, event.target.checked, groupValue, options);
				}}
			/>
		);
	},
	(prevProps, nextProps) => {
		const prevJob = prevProps.row.original;
		const nextJob = nextProps.row.original;

		return (
			prevJob.id === nextJob.id &&
			prevJob[prevProps.fieldKey] === nextJob[nextProps.fieldKey] &&
			prevProps.fieldKey === nextProps.fieldKey &&
			prevProps.groupByField === nextProps.groupByField &&
			prevProps.vm === nextProps.vm
		);
	},
);

interface CheckboxColumnAggregatedCellProps {
	row: MRT_Row<Job>;
	vm: CheckboxColumnViewModel;
	fieldKey: keyof Job;
	groupByField: keyof Job;
	groupStateKey?: string;
}

/**
 * Memoized AggregatedCell component for checkbox columns
 * Renders editable Checkbox when group row is in edit mode
 */
const CheckboxColumnAggregatedCell = React.memo(
	function CheckboxColumnAggregatedCell({
		row,
		vm,
		fieldKey,
		groupByField,
		groupStateKey,
	}: CheckboxColumnAggregatedCellProps) {
		// Get value from first job in group
		const job = row.original;
		const groupValue = String(job[groupByField] ?? "");
		const { isChecked, isIndeterminate } = vm.calculateGroupCheckboxState(
			groupValue,
			fieldKey,
			groupByField,
			groupStateKey,
		);

		return (
			<Checkbox
				checked={isChecked}
				indeterminate={isIndeterminate}
				onChange={(event) => {
					vm.handleGroupCheckboxChange(groupValue, fieldKey, event.target.checked, groupByField, groupStateKey);
				}}
			/>
		);
	},
	(prevProps, nextProps) => {
		const prevJob = prevProps.row.original;
		const nextJob = nextProps.row.original;

		return (
			prevJob.id === nextJob.id &&
			prevJob[prevProps.fieldKey] === nextJob[nextProps.fieldKey] &&
			prevProps.fieldKey === nextProps.fieldKey &&
			prevProps.groupByField === nextProps.groupByField &&
			prevProps.vm === nextProps.vm &&
			prevProps.groupStateKey === nextProps.groupStateKey
		);
	},
);

/**
 * Build a checkbox column
 * Uses memoized Cell components to prevent unnecessary re-renders when column definitions change
 */
export function checkboxColumn(
	context: ColumnContext,
	fieldKey: keyof Job,
	columnId: ImportTableColumn,
	options?: BooleanColumnOptions,
): MRT_ColumnDef<Job, unknown> {
	const vm = useCheckboxColumnViewModel(context);

	const {
		size = 100,
		minSize = 100,
		hasGroupUpdate = true,
		groupStateKey,
		groupByField = "importGrowerName",
	} = options || {};

	const displayName = columnDisplayNames[columnId];

	return {
		id: columnId,
		header: displayName,
		minSize,
		size,
		accessorKey: fieldKey as string,
		enableEditing: false,
		Cell: ({ row }: { row: MRT_Row<Job> }) => (
			<CheckboxColumnCell
				row={row}
				vm={vm}
				fieldKey={fieldKey}
				groupByField={groupByField}
				options={{
					hasGroupUpdate,
					groupStateKey,
					groupByField,
				}}
			/>
		),
		...(hasGroupUpdate &&
			groupByField && {
				AggregatedCell: ({ row }: { row: MRT_Row<Job> }) => (
					<CheckboxColumnAggregatedCell
						row={row}
						vm={vm}
						fieldKey={fieldKey}
						groupByField={groupByField}
						groupStateKey={groupStateKey}
					/>
				),
			}),
	};
}
